import { useCallback, useEffect, useState } from 'react';

import { useIsFeatureEnabled } from '@chessopen/play/featureToggle';
import { useAuth } from '@features/auth/hooks/useAuth';
import { useIsAdmin } from '@features/auth/hooks/useIsAdmin';
import {
  useActivateClubMemberMutation,
  useDeleteClubMemberMutation,
  useManagerClubMembersQuery,
  useRejectClubMemberMutation,
  useUpdateClubMemberAuxMutation,
} from '@features/club/api/hooks';
import { useClubGroupProps } from '@features/club/hooks/useClubGroupProps';
import { MemberAux } from '@features/club/typings';
import {
  ClubParticipantFormStatus,
  ClubParticipantsForm,
} from '@features/club/ui/ClubParticipantsForm/ClubParticipantsForm';
import { RcfAttributes } from '@features/shared/api/typings';
import { useRcfAttributesMutation, useRcfUsersQuery } from '@features/user/api/hooks';
import { RcfUserSearchContext } from '@features/user/ui/RcfUserSearch/RcfUserSearchContext';

type Props = {
  clubId: string;
};

export function ClubParticipantsFormWidget({ clubId }: Props) {
  const { mutate: onDelete, isSuccess: isDeleteSuccess } = useDeleteClubMemberMutation(clubId);
  const { mutate: onAccept, isSuccess: isAcceptSuccess } = useActivateClubMemberMutation(clubId);
  const { mutate: onReject, isSuccess: isRejectSuccess } = useRejectClubMemberMutation(clubId);
  const { mutateAsync: updateRcfAttributes } = useRcfAttributesMutation();
  const { mutateAsync: updateMemberAux } = useUpdateClubMemberAuxMutation(clubId);

  const [status, setStatus] = useState<ClubParticipantFormStatus>('Active');
  const { user } = useAuth();
  const isAdmin = useIsAdmin();
  const currentUserId = user?.userId;
  // колонка камер — только там, где клубу доступен видеоконтроль (как в редакторе турнира)
  const isVideoAvailable = useIsFeatureEnabled('tournamentVideo', clubId);

  const groupProps = useClubGroupProps(clubId);

  const [search, setSearch] = useState<string | null>(null);

  const {
    data: members,
    isPending,
    refetch,
    fetchNextPage,
    hasNextPage,
  } = useManagerClubMembersQuery(clubId, {
    sortBy: 'Alphabet',
    status,
    query: search || undefined,
  });

  const [query, setQuery] = useState('');
  const {
    data: rcfUsers,
    isFetching,
    hasNextPage: hasNextPageRcf,
    fetchNextPage: fetchNextPageRcf,
  } = useRcfUsersQuery({
    query,
    limit: 100,
  });

  const onChangeRcfAttributes = useCallback(
    async (value: RcfAttributes) => {
      const valueToSave: Partial<RcfAttributes> = { ...value };
      delete valueToSave.rcfIdApproved;

      await updateRcfAttributes({ items: [value] });
      refetch();
    },
    [updateRcfAttributes, refetch],
  );

  // требование к камерам на видеоконтроле: в aux участника клуба
  const onCamerasChange = useCallback(
    async (userId: string, aux: MemberAux | null) => {
      await updateMemberAux({ userId, aux });
      refetch();
    },
    [updateMemberAux, refetch],
  );

  useEffect(() => {
    if (isDeleteSuccess || isAcceptSuccess || isRejectSuccess) {
      refetch();
    }
  }, [isDeleteSuccess, isAcceptSuccess, isRejectSuccess, refetch]);

  useEffect(() => {
    if (search !== null) {
      refetch();
    }
  }, [search, refetch]);

  return (
    <RcfUserSearchContext.Provider
      value={{
        query,
        setQuery,
        rcfUsers: query && rcfUsers ? rcfUsers : [],
        isLoading: isFetching,
        hasNextPage: hasNextPageRcf,
        fetchNextPage: fetchNextPageRcf,
      }}
    >
      <ClubParticipantsForm
        participants={members || []}
        onDelete={onDelete}
        onAccept={onAccept}
        onReject={onReject}
        isLoading={isPending}
        status={status}
        setStatus={setStatus}
        currentUserId={currentUserId}
        onLoadMore={hasNextPage ? fetchNextPage : undefined}
        onSearch={setSearch}
        isRcf={isAdmin}
        onRcfAttributesChange={onChangeRcfAttributes}
        onCamerasChange={isVideoAvailable ? onCamerasChange : undefined}
        {...groupProps}
      />
    </RcfUserSearchContext.Provider>
  );
}
